'use client'

import { ArrowUpRight, ChevronRight } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { appendWorkspaceScopeToPath } from '@/utils/workspace-scope'

import { NavSeparator } from './nav-separator'

export function NavMain({
  items,
  onItemClick
}: {
  items: {
    title: string
    url: string
    icon?: any
    isActive?: boolean
    hasSeparator?: boolean
    external?: boolean
    hasSubmenu?: boolean
    disabled?: boolean
    badgeLabel?: string
    badgeTone?: string
  }[]
  onItemClick?: (url: string) => void
}) {
  // Grab the current URL/path from Next.js
  const currentPath = usePathname()
  const router = useRouter()
  const { state } = useSidebar()
  const { activeTeamId } = useWorkspace()

  const handleNavigation = (url: string, isExternal: boolean = false) => {
    if (onItemClick) {
      onItemClick(url)
    } else if (isExternal) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      router.push(appendWorkspaceScopeToPath(url, activeTeamId))
    }
  }

  const handleMouseDown = (event: React.MouseEvent, url: string, isExternal: boolean = false) => {
    // Handle middle click (button 1)
    if (event.button === 1) {
      event.preventDefault()
      if (isExternal) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        window.open(appendWorkspaceScopeToPath(url, activeTeamId), '_blank', 'noopener,noreferrer')
      }
    }
  }

  return (
    <SidebarGroup className="pt-2">
      <SidebarMenu>
        {items.map((item, index) => {
          const isActive = (() => {
            if (item.disabled) return false
            const url = item.url
            if (url === '/agents/home')
              return (
                currentPath === '/agent' ||
                currentPath === '/new-agent' ||
                currentPath === '/v2/new-agent' ||
                currentPath === '/agents/home' ||
                currentPath.startsWith('/agent/chats') ||
                currentPath.startsWith('/agent-ide') ||
                currentPath.startsWith('/v2/agent-ide') ||
                currentPath.startsWith('/agents/')
              )
            if (url.startsWith('/agent/'))
              return currentPath.startsWith(url)
            if (url === '/tools')
              return currentPath.startsWith('/mol') || currentPath.startsWith('/py2dmol') || currentPath.startsWith('/code')
            if (url === '/sandbox')
              return currentPath.startsWith('/design') || currentPath.startsWith('/properties') || currentPath.startsWith('/fold')
            if (url === '/workflows/templates')
              return currentPath.startsWith('/workflows') || currentPath.startsWith('/workflow')
            if (url === '/integrations')
              return currentPath.startsWith('/integrations')
            if (url === '/jobs')
              return currentPath.startsWith('/jobs') || currentPath.startsWith('/sequences') || currentPath.startsWith('/tasks/results')
            if (url === '/projects')
              return currentPath.startsWith('/projects') || currentPath.startsWith('/project')
            if (url === '/teams')
              return currentPath.startsWith('/teams')
            if (url === '/settings')
              return currentPath.startsWith('/settings') || /\/teams\/[^/]+\/(settings|members)(?:\/|$)/.test(currentPath)
            return currentPath === url
          })()

          return (
            <div key={item.title}>
              <SidebarMenuItem>
                              <div 
                onClick={() => !item.disabled && handleNavigation(item.url, item.external)}
                onMouseDown={(e) => !item.disabled && handleMouseDown(e, item.url, item.external)}
                className={item.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
              >
                  <SidebarMenuButton
                    tooltip={item.disabled && item.badgeLabel ? `${item.title} (${item.badgeLabel})` : item.title}
                    size="lg"
                    disabled={item.disabled}
                    className={`${state === 'collapsed' ? 'justify-end' : ''} !h-10 ${item.disabled ? 'opacity-70' : ''}`}
                    style={
                      isActive
                        ? { backgroundColor: 'hsl(var(--sidebar-accent))' }
                        : {}
                    }
                  >
                    <div className={`flex items-center w-full ${state === 'collapsed' ? 'justify-end' : ''}`}>
                      {item.icon && (
                        <item.icon
                          className={`!h-5 !w-5 ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}
                        />
                      )}
                      {state === 'expanded' && (
                        <div className="flex items-center w-full pl-2">
                          <span
                            className={`text-sm ${
                              isActive ? 'font-medium text-foreground' : 'font-normal text-muted-foreground'
                            }`}
                          >
                            {item.title}
                          </span>
                          {item.badgeLabel ? (
                            <span
                              className={[
                                'ml-2 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none',
                                item.badgeTone === 'pink'
                                  ? 'bg-pink-500/15 text-pink-600 dark:text-pink-400'
                                  : item.badgeTone === 'yellow'
                                    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                                    : item.badgeTone === 'gray'
                                      ? 'bg-muted text-muted-foreground'
                                      : 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                              ].join(' ')}
                            >
                              {item.badgeLabel}
                            </span>
                          ) : null}
                          {item.hasSubmenu ? (
                            <ChevronRight
                              className={`ml-auto h-4 w-4 ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}
                            />
                          ) : item.external ? (
                            <ArrowUpRight
                              className={`ml-auto h-3 w-3 ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </SidebarMenuButton>
                </div>
              </SidebarMenuItem>
              {item.hasSeparator && index < items.length - 1 && <NavSeparator />}
            </div>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
